import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import useResultService from "../../api/services/ResultsService";
import LoadingSpinner from "../../components/LoadingSpinner";
import generateTermResultsPDF from "../../utils/termResultsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import { Article } from "@mui/icons-material";

const ClassTermReport = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const termId = location.state?.termId;
  const termName = location.state?.termName;
  const classId = location.state?.id;

  const { getResultsByTermAndClass, sendSmsToParents } = useResultService();

  // ✅ FIX: keep service functions stable (prevents loops if service functions change refs)
  const getResultsByTermAndClassRef = useRef(getResultsByTermAndClass);
  const sendSmsToParentsRef = useRef(sendSmsToParents);

  useEffect(() => {
    getResultsByTermAndClassRef.current = getResultsByTermAndClass;
  }, [getResultsByTermAndClass]);

  useEffect(() => {
    sendSmsToParentsRef.current = sendSmsToParents;
  }, [sendSmsToParents]);

  // store ALL students (unfiltered)
  const [allResults, setAllResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ NEW: status filter (default ACTIVE)
  const [statusView, setStatusView] = useState("ACTIVE"); // "ACTIVE" | "ALL"

  const [sendingStatus, setSendingStatus] = useState({});
  const [sentStatus, setSentStatus] = useState({});

  const getGradePoint = (grade, classType) => {
    if (classType === "A-LEVEL") {
      const gradePoints = { A: 1, B: 2, C: 3, D: 4, E: 5, S: 6, F: 7 };
      return gradePoints[grade] || 0;
    } else {
      switch (grade) {
        case "A":
          return 1;
        case "B":
          return 2;
        case "C":
          return 3;
        case "D":
          return 4;
        case "F":
          return 5;
        default:
          return 0;
      }
    }
  };

  const getDivisionLabel = (points, classType) => {
    if (classType === "A-LEVEL") {
      const p = parseFloat(points).toFixed(1).replace(/\.0$/, "");
      if (points >= 1 && points <= 9) return `Division I • ${p}`;
      if (points >= 10 && points <= 12) return `Division II • ${p}`;
      if (points >= 13 && points <= 17) return `Division III • ${p}`;
      if (points >= 18 && points <= 21) return `Division IV • ${p}`;
      return `Division 0 • ${p}`;
    } else {
      if (points >= 1 && points <= 17) return `Division I • ${points}`;
      if (points <= 21) return `Division II • ${points}`;
      if (points <= 25) return `Division III • ${points}`;
      if (points <= 33) return `Division IV • ${points}`;
      return `Division 0 • ${points}`;
    }
  };

  const hasAllZeroValues = (details) => {
    return (
      (!details.exam10Average || details.exam10Average === 0) &&
      (!details.exam40Average || details.exam40Average === 0) &&
      (!details.exam50Marks || details.exam50Marks === 0)
    );
  };

  const getGradedSubjects = (student) => {
    if (!student?.subjects || !student.classType) return [];

    const aLevelSubjectsToConsider = [
      "ADVANCED MATHEMATICS",
      "ENGLISH LANGUAGE",
      "PHYSICS",
      "CHEMISTRY",
      "BIOLOGY",
      "HISTORY",
      "ECONOMICS",
      "GEOGRAPHY",
    ];

    const subjectsWithGrades = [];

    Object.entries(student.subjects).forEach(([subject, details]) => {
      const subjectUpper = subject.toUpperCase();

      if (student.classType === "A-LEVEL" && !aLevelSubjectsToConsider.includes(subjectUpper)) {
        return;
      }

      if (!hasAllZeroValues(details) && details.grade) {
        const gradePoint = getGradePoint(details.grade, student.classType);
        if (gradePoint > 0) {
          subjectsWithGrades.push({
            subject,
            grade: details.grade,
            points: gradePoint,
          });
        }
      }
    });

    return subjectsWithGrades;
  };

  const getBestSevenGradePoints = (student) => {
    const subjectsWithGrades = getGradedSubjects(student);
    // Select the 7 best grades (smallest grade points = better performance)
    subjectsWithGrades.sort((a, b) => a.points - b.points);
    return subjectsWithGrades.slice(0, 7);
  };

  const calculateDivision = (student) => {
    if (!student?.subjects || !student.classType) return "N/A";

    const aLevelSubjectsToConsider = [
      "ADVANCED MATHEMATICS",
      "ENGLISH LANGUAGE",
      "PHYSICS",
      "CHEMISTRY",
      "BIOLOGY",
      "HISTORY",
      "ECONOMICS",
      "GEOGRAPHY",
    ];

    // A-LEVEL division uses all considered subjects (existing school rule)
    if (student.classType === "A-LEVEL") {
      let totalPoints = 0;
      let subjectCount = 0;

      Object.entries(student.subjects).forEach(([subject, details]) => {
        const subjectUpper = subject.toUpperCase();
        if (!aLevelSubjectsToConsider.includes(subjectUpper)) return;

        if (!hasAllZeroValues(details) && details.grade) {
          const gradePoint = getGradePoint(details.grade, student.classType);
          totalPoints += gradePoint;
          subjectCount++;
        }
      });

      if (subjectCount === 0) return "N/A";
      return getDivisionLabel(totalPoints, student.classType);
    }

    // O-LEVEL division uses the 7 best subjects
    const top7Subjects = getBestSevenGradePoints(student);
    if (top7Subjects.length === 0) return "N/A";

    const totalPoints = top7Subjects.reduce((sum, subj) => sum + subj.points, 0);
    return getDivisionLabel(totalPoints, student.classType);
  };

  // Student GPA = sum of 7 best grade points ÷ 7 (only when student has 7+ subjects)
  const calculateStudentGpa = (student) => {
    const gradedSubjects = getGradedSubjects(student);
    if (gradedSubjects.length < 7) return null;

    gradedSubjects.sort((a, b) => a.points - b.points);
    const top7Subjects = gradedSubjects.slice(0, 7);
    const totalPoints = top7Subjects.reduce((sum, subj) => sum + subj.points, 0);
    return totalPoints / 7;
  };

  const formatGpa = (gpa) => {
    if (gpa === null || gpa === undefined || Number.isNaN(gpa)) return "N/A";
    return Number(gpa).toFixed(4);
  };

  // Load sent status from localStorage on component mount
  useEffect(() => {
    const savedSentStatus = localStorage.getItem(`sentStatus_${classId}_${termId}`);
    if (savedSentStatus) setSentStatus(JSON.parse(savedSentStatus));
  }, [classId, termId]);

  // ✅ Subjects that should NOT be displayed
  const excludedSubjects = [
    "CHEMISTRY-1",
    "CHEMISTRY-2",
    "CHEMISTRY-3",
    "PHYSICS-1",
    "PHYSICS-2",
    "PHYSICS-3",
    "BIOLOGY-1",
    "BIOLOGY-2",
    "BIOLOGY-3",
    "HISTORY-1",
    "HISTORY-2",
    "GEOGRAPHY-1",
    "MATHEMATICS-1",
    "MATHEMATICS-2",
    "ECONOMICS-1",
    "ECONOMICS-2",
    "GEOGRAPHY-2",
  ];
  const excludedSubjectsUpper = excludedSubjects.map((s) => s.toUpperCase());

  // ✅ Fetch term results (only once per term/class change)
  useEffect(() => {
    let mounted = true;

    const fetchResults = async () => {
      if (!termId || !classId) return;

      setIsLoading(true);
      try {
        const data = await getResultsByTermAndClassRef.current(termId, classId);

        if (!mounted) return;

        if (data?.data) {
          const firstResult = data.data?.[0];
          const className = data.className || firstResult?.className || "N/A";
          const classTitle = data.classTitle || firstResult?.classTitle || "N/A";
          const resolvedTermName =
            data.termName ||
            firstResult?.termName ||
            data.term?.name ||
            data.term?.termName ||
            firstResult?.term?.name ||
            firstResult?.term?.termName ||
            "N/A";

          const processed = data.data.map((student) => {
            const division = calculateDivision(student);
            const gpa = calculateStudentGpa(student);
            return {
              ...student,
              className,
              classTitle,
              termName: resolvedTermName,
              division,
              gpa,
              // ✅ ensure we have studentStatus for filtering
              studentStatus: student.studentStatus || student.status || "",
            };
          });

          setAllResults(processed);
        } else {
          setAllResults([]);
        }
      } catch (err) {
        console.error("Error fetching results:", err);
        if (mounted) setAllResults([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchResults();

    return () => {
      mounted = false;
    };
  }, [termId, classId]);

  // ✅ filter results by ACTIVE / ALL
  const results = useMemo(() => {
    const isActive = (s) =>
      String(s || "")
        .trim()
        .toUpperCase() === "ACTIVE";

    if (statusView === "ALL") return allResults;
    return allResults.filter((s) => isActive(s.studentStatus));
  }, [allResults, statusView]);

  // Class GPA = average of individual student GPAs currently displayed
  const classGpa = useMemo(() => {
    const validGpas = results
      .map((student) => student.gpa)
      .filter((gpa) => gpa !== null && gpa !== undefined && !Number.isNaN(gpa));

    if (validGpas.length === 0) return null;

    const total = validGpas.reduce((sum, gpa) => sum + gpa, 0);
    return total / validGpas.length;
  }, [results]);

  // ---------- SMS ----------
  const handleSendSMS = async (row) => {
    const studentId = row.studentId;
    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const hasSubjectResults = (subj) =>
        subj.exam10Average > 0 || subj.exam40Average > 0 || subj.exam50Marks > 0;

      const subjectsSummary = Object.entries(row.subjects || {}).reduce((acc, [subject, details]) => {
        if (!excludedSubjectsUpper.includes(subject.toUpperCase()) && hasSubjectResults(details)) {
          acc[subject] = { marks: details?.totalMarks || 0 };
        }
        return acc;
      }, {});

      const extractNumericPosition = (positionText) => {
        if (!positionText) return 0;
        const match = positionText.match(/^(\d+)/);
        return match ? parseInt(match[1]) : 0;
      };

      const payload = {
        studentName: row.studentName,
        studentNumber: row.studentNumber,
        combination: row.combination || "N/A",
        subjects: subjectsSummary,
        totalMarks: parseFloat(row.totalMarks) || 0,
        average: parseFloat(row.average) || 0,
        position: extractNumericPosition(row.positionText),
        division: row.division || "N/A",
      };

      await sendSmsToParentsRef.current(studentId, payload);

      setSentStatus((prev) => {
        const newStatus = { ...prev, [studentId]: true };
        localStorage.setItem(`sentStatus_${classId}_${termId}`, JSON.stringify(newStatus));
        return newStatus;
      });
    } catch (error) {
      console.error("❌ Error sending SMS", error);
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  // Columns
  const createColumns = () => {
    if (!results.length) return [];

    const subjectSet = new Set();
    results.forEach((student) => {
      Object.keys(student.subjects || {}).forEach((subj) => {
        if (!excludedSubjectsUpper.includes(subj.toUpperCase())) subjectSet.add(subj);
      });
    });

    const subjectNames = Array.from(subjectSet);

    const hasSubjectResults = (subj) =>
      subj.exam10Average > 0 || subj.exam40Average > 0 || subj.exam50Marks > 0;

    const columns = [
      { field: "studentName", headerName: "Student Name", flex: 1, minWidth: 150 },
      { field: "studentNumber", headerName: "Index Number", flex: 1, minWidth: 150 },
      {
        field: "combination",
        headerName: "Combination",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.combination || "N/A"}</Typography>
        ),
      },
    ];

    // Subject columns
    subjectNames.forEach((subject) => {
      columns.push({
        field: `subject_${subject}`,
        headerName: subject,
        flex: 1.5,
        minWidth: 220,
        renderCell: ({ row }) => {
          const subj = row.subjects?.[subject];
          if (!subj) return null;

          if (!hasSubjectResults(subj)) {
            return (
              <Typography color="text.secondary" fontStyle="italic">
                No results
              </Typography>
            );
          }

          return (
            <Box display="flex" flexDirection="column">
              {subj.exam10Average > 0 && <Typography>Exam10 Avg: {subj.exam10Average}</Typography>}
              {subj.exam40Average > 0 && <Typography>Exam40 Avg: {subj.exam40Average}</Typography>}
              {subj.exam50Marks > 0 && <Typography>Exam50 Marks: {subj.exam50Marks}</Typography>}
              <Typography fontWeight="bold">Total: {subj.totalMarks}</Typography>
              <Typography fontWeight="bold" color="secondary.main">
                Grade: {subj.grade ?? "-"}
              </Typography>
            </Box>
          );
        },
      });
    });

    // Final Summary Columns
    columns.push(
      { field: "totalMarks", headerName: "Total Marks", flex: 0.7, minWidth: 120 },
      { field: "average", headerName: "Average", flex: 0.7, minWidth: 120 },
      { field: "positionText", headerName: "Position", flex: 0.7, minWidth: 120 },
      {
        field: "division",
        headerName: "Division",
        flex: 0.8,
        minWidth: 140,
        renderCell: ({ row }) => (
          <Typography
            fontWeight="bold"
            color={
              row.division?.includes("Division I")
                ? "success.main"
                : row.division?.includes("Division II")
                ? "info.main"
                : row.division?.includes("Division III")
                ? "warning.main"
                : row.division?.includes("Division IV")
                ? "error.main"
                : "text.secondary"
            }
          >
            {row.division || "N/A"}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.9,
        minWidth: 180,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1} alignItems="center">
            <IconButton
              color="secondary"
              onClick={() => navigate(`/term_student_results/${row.studentId}/${classId}/${termId}`)}
            >
              <DownloadIcon />
            </IconButton>

            <Button
              variant="contained"
              size="small"
              color="secondary"
              disabled={sendingStatus[row.studentId]}
              onClick={() => handleSendSMS(row)}
              sx={{
                backgroundColor: sentStatus[row.studentId] ? "#0A6927" : undefined,
                color: sentStatus[row.studentId] ? "white" : undefined,
              }}
            >
              {sendingStatus[row.studentId]
                ? "Sending..."
                : sentStatus[row.studentId]
                ? "Sent"
                : "Send SMS"}
            </Button>

            <IconButton
              color="secondary"
              onClick={() => navigate(`/student_evaluation_report/${termId}/${row.studentId}`)}
            >
              <Article />
            </IconButton>
          </Box>
        ),
      }
    );

    return columns;
  };

  const headerTitle = `CLASS TERM REPORT FOR CLASS NAME: ${
    (results[0]?.className || allResults[0]?.className || "N/A")
  } | CLASS TITLE: ${
    (results[0]?.classTitle || allResults[0]?.classTitle || "N/A")
  } (${
    (results[0]?.classType || allResults[0]?.classType || "N/A")
  })`;

  return (
    <Box m="20px">
      <Header title={headerTitle} />

      {/* Class GPA + ACTIVE / ALL + PDF EXPORT (same row) */}
      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
          <Typography variant="h6" fontWeight={700} color="#000000">
            Class GPA
          </Typography>
          <Typography variant="h5" fontWeight={800} color="#000000">
            {formatGpa(classGpa)}
          </Typography>
          <Typography variant="body2" color="#000000">
            Average of student GPAs (each from 7 best subjects)
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Button
            variant={statusView === "ACTIVE" ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setStatusView("ACTIVE")}
          >
            ACTIVE
          </Button>
          <Button
            variant={statusView === "ALL" ? "contained" : "outlined"}
            color="secondary"
            onClick={() => setStatusView("ALL")}
          >
            ALL
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<DownloadIcon />}
            onClick={() =>
              generateTermResultsPDF(
                results, // ✅ export what is currently displayed (ACTIVE or ALL)
                termName || "N/A",
                (results[0]?.className || allResults[0]?.className || "N/A")
              )
            }
            disabled={!results || results.length === 0}
            sx={{ ml: 1 }}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : results.length > 0 ? (
        <Box sx={{ height: "75vh" }}>
          <DataGrid
            rows={results.map((s, idx) => ({ ...s, id: s.studentId || idx }))}
            columns={createColumns()}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            getRowHeight={() => 120}
          />
        </Box>
      ) : (
        <Typography textAlign="center" mt={4}>
          {statusView === "ACTIVE"
            ? "No ACTIVE students results found for this term and class"
            : "No results found for this term and class"}
        </Typography>
      )}
    </Box>
  );
};

export default ClassTermReport;
